import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../SEO'
import { SplitSection, StackRibbon } from '../ServicePage'
import ContactForm from '../ContactForm'
import { trackCtaClick } from '../../utils/analytics'
import './../../styles/ServicePage.css'

/**
 * Hero visual for the Social Media service page — animated stylised phone
 * with a feed, floating heart / comment / share icons, and a camera lens
 * mark to nod to the photography work.
 */
const SocialMediaHero = () => (
  <svg viewBox="0 0 460 460" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="sm-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0066ff" />
        <stop offset="100%" stopColor="#00d4ff" />
      </linearGradient>
      <linearGradient id="sm-pink" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ff6b9d" />
        <stop offset="100%" stopColor="#ff8a5b" />
      </linearGradient>
      <radialGradient id="sm-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#00d4ff" stopOpacity="0" />
      </radialGradient>
    </defs>

    <circle cx="230" cy="230" r="200" fill="url(#sm-glow)">
      <animate attributeName="r" values="200;215;200" dur="6s" repeatCount="indefinite" />
    </circle>

    {/* phone */}
    <g>
      <rect x="155" y="70" width="160" height="320" rx="26" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.20)" />
      <rect x="165" y="80" width="140" height="300" rx="20" fill="rgba(0,0,0,0.20)" />
      {/* notch */}
      <rect x="215" y="74" width="40" height="6" rx="3" fill="rgba(255,255,255,0.20)" />

      {/* feed post 1 */}
      <g>
        <circle cx="180" cy="106" r="9" fill="url(#sm-pink)" />
        <rect x="194" y="100" width="60" height="6" rx="3" fill="rgba(255,255,255,0.85)" />
        <rect x="194" y="110" width="40" height="5" rx="2.5" fill="rgba(255,255,255,0.40)" />
        <rect x="170" y="124" width="130" height="78" rx="8" fill="url(#sm-grad)" opacity="0.9">
          <animate attributeName="opacity" values="0.7;1;0.7" dur="3.4s" repeatCount="indefinite" />
        </rect>
        {/* heart, comment, share row */}
        <path d="M174 212 a4 4 0 1 1 6 0 a4 4 0 1 1 6 0 v0 q0 4 -6 9 q-6 -5 -6 -9z" fill="rgba(255,107,157,0.95)" />
        <circle cx="195" cy="216" r="4" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="1.4" />
        <path d="M210 213 l8 5 -8 5 v-3 l-3 0 0 -4 3 0z" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="1.4" strokeLinejoin="round" />
        <text x="244" y="219" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="rgba(255,255,255,0.55)">12.4K</text>
      </g>

      {/* feed post 2 */}
      <g>
        <circle cx="180" cy="248" r="9" fill="url(#sm-grad)" />
        <rect x="194" y="242" width="60" height="6" rx="3" fill="rgba(255,255,255,0.85)" />
        <rect x="194" y="252" width="40" height="5" rx="2.5" fill="rgba(255,255,255,0.40)" />
        <rect x="170" y="266" width="130" height="80" rx="8" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.10)" />
        <path d="M220 296 l16 10 -16 10 v-20z" fill="rgba(0,212,255,0.85)" />
        <rect x="170" y="356" width="60" height="5" rx="2.5" fill="rgba(255,255,255,0.30)" />
      </g>

      {/* bottom nav dots */}
      <g>
        <circle cx="200" cy="378" r="2" fill="rgba(255,255,255,0.7)" />
        <circle cx="220" cy="378" r="2" fill="rgba(255,255,255,0.40)" />
        <circle cx="240" cy="378" r="2" fill="rgba(255,255,255,0.40)" />
      </g>

      <animateTransform attributeName="transform" type="translate" values="0 0;0 -8;0 0" dur="6s" repeatCount="indefinite" />
    </g>

    {/* floating heart badge */}
    <g>
      <circle cx="380" cy="120" r="24" fill="url(#sm-pink)" />
      <path d="M370 120 a4 4 0 1 1 8 0 a4 4 0 1 1 8 0 q0 6 -8 12 q-8 -6 -8 -12z" fill="#fff" />
      <animateTransform attributeName="transform" type="translate" values="0 0;0 -10;0 0" dur="4.5s" repeatCount="indefinite" />
    </g>

    {/* floating camera/lens (nod to photography) */}
    <g>
      <circle cx="80" cy="120" r="26" fill="rgba(255,255,255,0.06)" stroke="rgba(0,212,255,0.55)" strokeWidth="1.5" />
      <circle cx="80" cy="120" r="14" fill="none" stroke="#00d4ff" strokeWidth="2" />
      <circle cx="80" cy="120" r="6" fill="url(#sm-grad)" />
      <circle cx="86" cy="115" r="2" fill="rgba(255,255,255,0.85)" />
      <animateTransform attributeName="transform" type="translate" values="0 0;0 -6;0 0" dur="5.2s" repeatCount="indefinite" />
    </g>

    {/* floating play badge */}
    <g>
      <circle cx="100" cy="360" r="22" fill="rgba(255,255,255,0.06)" stroke="rgba(0,212,255,0.45)" strokeWidth="1.5" />
      <path d="M93 350 l16 10 -16 10 v-20z" fill="url(#sm-grad)" />
      <animateTransform attributeName="transform" type="translate" values="0 0;0 -8;0 0" dur="4.8s" repeatCount="indefinite" />
    </g>

    {/* hashtag + sparkles */}
    <g fontFamily="Inter, sans-serif" fontWeight="700" fill="#00d4ff" fontSize="22">
      <text x="370" y="320">#</text>
      <animate attributeName="opacity" values="0.4;1;0.4" dur="2.6s" repeatCount="indefinite" />
    </g>
    <circle cx="350" cy="220" r="2.4" fill="#7aa2ff">
      <animate attributeName="opacity" values="0.3;1;0.3" dur="2.4s" repeatCount="indefinite" />
    </circle>
    <circle cx="60" cy="240" r="2" fill="#00d4ff">
      <animate attributeName="opacity" values="0.4;1;0.4" dur="3.1s" repeatCount="indefinite" />
    </circle>
  </svg>
)

const SocialMedia = () => (
  <>
    <SEO
      path="/marketing/social-media"
      title="Social Media Management — Strategy, Content & Photography"
      description="Social media management from QA Tech: strategy, content creation, photography & video, community management and paid social. We help brands show up consistently on Instagram, TikTok, LinkedIn and beyond."
      keywords="social media management, social media strategy, content creation, brand photography, video production, community management, paid social, Instagram, TikTok, LinkedIn marketing, social media UK"
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'Social Media Management',
        provider: {
          '@type': 'Organization',
          name: 'QA Tech',
          url: 'https://qatechltd.com',
        },
        areaServed: 'Worldwide',
        description:
          'Strategy, content creation, photography & video, community management and paid social — tailored to your brand and audience.',
        url: 'https://qatechltd.com/marketing/social-media',
      }}
    />

    <div className="body sp-body">
      <header className="sp-hero">
        <div className="sp-hero__bg" aria-hidden="true">
          <span className="sp-hero__orb sp-hero__orb--a" />
          <span className="sp-hero__orb sp-hero__orb--b" />
          <span className="sp-hero__grid" />
        </div>
        <div className="sp-hero__inner">
          <nav className="sp-breadcrumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">/</span>
            <Link to="/marketing">Marketing</Link>
            <span aria-hidden="true">/</span>
            <span>Social Media</span>
          </nav>

          <div className="sp-hero__content">
            <span className="sp-hero__eyebrow">Service / Social Media</span>
            <h1 className="sp-hero__title">
              Stories your brand actually wants to be telling.
            </h1>
            <p className="sp-hero__lede">
              Strategy, content, photography and day-to-day management
              &mdash; across every platform that matters to your audience.
              Built around your brand, not a one-size template.
            </p>
            <div className="sp-hero__cta-row">
              <a
                href="#sp-contact"
                className="sp-cta-primary"
                onClick={() => trackCtaClick('Start a conversation', 'marketing_social_media_hero')}
              >
                Start a conversation
              </a>
              <Link to="/marketing" className="sp-cta-secondary">
                Back to marketing services &rarr;
              </Link>
            </div>
          </div>

          <div className="sp-hero__visual" aria-hidden="true">
            <SocialMediaHero />
          </div>
        </div>
      </header>

      <main className="sp-main">
        <SplitSection
          index={1}
          align="left"
          kicker="Strategy first"
          title="A plan, before a single post."
        >
          <p>
            Most social media work fails because nobody agreed what success
            looks like. We start with the unsexy bit: who are we talking to,
            what does your brand sound like, what content earns its place,
            and what are we actually trying to move?
          </p>
          <ul>
            <li>Brand voice &amp; tone of voice guidelines</li>
            <li>Content pillars &amp; monthly themes</li>
            <li>Audience research &amp; competitive scan</li>
            <li>Posting cadence per platform</li>
            <li>KPIs and a measurement plan that ties to the business</li>
          </ul>
        </SplitSection>

        <SplitSection
          index={2}
          align="right"
          kicker="Content creation"
          title="Posts, captions, reels — all on-brand, all on time."
        >
          <p>
            Static posts, carousels, reels, short-form video, stories and
            copy that sounds like a human wrote it &mdash; produced on a
            steady cadence so your feed never goes quiet. Every asset is
            on-brand by design, not by retrofit.
          </p>
          <ul>
            <li>Static graphics &amp; carousel design</li>
            <li>Short-form video &amp; reels</li>
            <li>Long-form video edits</li>
            <li>Copy &amp; captions written for the platform</li>
            <li>Stories, polls and interactive formats</li>
          </ul>
        </SplitSection>

        <SplitSection
          index={3}
          align="left"
          kicker="Photography &amp; video"
          title="Real shoots. Real assets. None of the AI-template look."
        >
          <p>
            Product shots, lifestyle and brand photography, behind-the-scenes
            and event coverage &mdash; produced and edited to feel like your
            brand on a good day. We work with real cameras, real lighting and
            real attention to detail, so you stop relying on stock.
          </p>
          <ul>
            <li>Product photography</li>
            <li>Brand &amp; lifestyle shoots</li>
            <li>Behind-the-scenes &amp; team content</li>
            <li>Event coverage</li>
            <li>Edit, colour, retouch &amp; export to the platform&rsquo;s sweet spot</li>
          </ul>
        </SplitSection>

        <SplitSection
          index={4}
          align="right"
          kicker="Day-to-day management"
          title="Always-on, without burning out your team."
        >
          <p>
            Scheduling, posting, replying, commenting, reacting, escalating,
            and keeping an eye on what people are saying about your brand.
            We treat your inbox and comments like a real channel &mdash;
            because that&rsquo;s where most of the relationship actually
            happens.
          </p>
          <ul>
            <li>Scheduling &amp; publishing across platforms</li>
            <li>Community management &mdash; DMs, comments, mentions</li>
            <li>Real-time engagement on time-sensitive posts</li>
            <li>Reputation monitoring &amp; escalation paths</li>
          </ul>
        </SplitSection>

        <SplitSection
          index={5}
          align="left"
          kicker="Paid social &amp; growth"
          title="Make every pound work harder."
        >
          <p>
            Boosted posts and full paid social campaigns &mdash; Meta Ads,
            TikTok Ads, LinkedIn Ads &mdash; built around your goal:
            awareness, engagement, lead generation or sales. Creative is
            tested, audiences are targeted properly, and conversions are
            tracked end-to-end (including the GA4 setup we already do).
          </p>
          <ul>
            <li>Meta Ads (Instagram &amp; Facebook)</li>
            <li>TikTok Ads</li>
            <li>LinkedIn Ads (B2B)</li>
            <li>Audience targeting &amp; lookalikes</li>
            <li>Creative testing &amp; iteration</li>
            <li>Conversion tracking aligned to GA4</li>
          </ul>
        </SplitSection>

        <SplitSection
          index={6}
          align="right"
          kicker="Reporting"
          title="Numbers your team will actually read."
        >
          <p>
            Monthly reports that explain the &ldquo;so what&rdquo;: what worked,
            what didn&rsquo;t, what we&rsquo;re changing, and what your money
            bought you. No jargon, no vanity metrics &mdash; just the truth,
            and the next move.
          </p>
        </SplitSection>

        <StackRibbon
          label="Platforms we cover"
          items={[
            'Instagram',
            'TikTok',
            'LinkedIn',
            'Facebook',
            'X (Twitter)',
            'YouTube',
            'Pinterest',
            'Threads',
          ]}
        />

        <StackRibbon
          label="Tools &amp; tech"
          items={[
            'Adobe Photoshop',
            'Adobe Lightroom',
            'Adobe Premiere Pro',
            'Capture One',
            'Final Cut Pro',
            'Meta Business Suite',
            'TikTok Creative Center',
            'LinkedIn Campaign Manager',
            'Buffer / Later',
            'Google Analytics 4',
          ]}
        />
      </main>

      <section className="sp-cta-section">
        <div className="sp-cta-section__inner">
          <h2>Ready for a feed that sounds like you?</h2>
          <p>
            Tell us what your brand stands for and what you&rsquo;re trying
            to grow &mdash; we&rsquo;ll come back with a plan and a fair
            quote.
          </p>
        </div>
      </section>

      <div id="sp-contact">
        <ContactForm />
      </div>
    </div>
  </>
)

export default SocialMedia
